import { defineStore, storeToRefs } from 'pinia'
import type { Package } from '@/types/package'
import { computed, reactive, ref, watch } from 'vue'
import { MemberType, MemberCycle, ButtonStatus, ButtonText, judgeMemberButtonState } from '@/store/user/memberButton'

// enum MemberType {
//     PRO = 'pro',
//     PLUS = 'plus'
// }

import shareDiscountsForMembers from '@/assets/images/package/shareDiscountsForMembers.svg'
import memberBenefitsSharing from '@/assets/images/package/memberBenefitsSharing.svg'
import spiritSeatManagement from '@/assets/images/package/spiritSeatManagement.svg'
import teamSpaceEfficiency from '@/assets/images/package/teamSpaceEfficiency.svg'
import teamExclusiveVIPService from '@/assets/images/package/teamExclusiveVIPService.svg'

import goldenChecked from '@/assets/images/package/goldenChecked.svg'
import grayChecked from '@/assets/images/package/grayChecked.svg'
import { getPackageMoney, getPackageOrigin } from '@/assets/pay'
import { usePayStore } from '@/store/pay'
import { useUserStore } from '@/store/user'
import { ModalType, useTeamEditionStore } from '@/store/teamEdition'
import { getExistCorporateOrder } from '@/views/power/service'
import { useEventTracking } from '@/hooks/useEventTracking'
import { POWER_EXHAUSTED_BUTTON_TEXT, TRIAL_EXPIRED_BUTTON_TEXT, useTeamTrialStore } from './teamTrial'
import { useActivityCheck } from '@/hooks/useActivityCheck'


const getButtonStatus = (btnTxt: ButtonText) => {
    // member_activate	从会员中心页点击「立即开通」按钮（禁用态按钮的点击、升级会员按钮的点击不算）
    // member_upgrade	从会员中心页点击「升级会员」按钮
    // member_renew	从会员中心页点击「续费会员」按钮
    switch (btnTxt) {
        case ButtonText.FREE_TRIAL:
        case ButtonText.OPEN_MEMBER:
            return 'member_activate'
        case ButtonText.UPGRADE_MEMBER:
            return 'member_upgrade'
        case ButtonText.RENEW_MEMBER:
            return 'member_renew'
    }
}



export const usePackageStore = defineStore('package', () => {
    const userStore = useUserStore();
    const teamEditionStore = useTeamEditionStore();

    // 是否使用planB
    const isPlanB = computed(() => {
        // https://www.teambition.com/task/67f775f57d9e24d4a5817b5e
        // 2025-04-10 下线B组测试
        // return userStore.userInfo.teamMemberAbtest
        return false
    })

    // 是否按年购买标准版本，或者专业版本
    const isYearly = reactive({
        [MemberType.PRO]: true,
        [MemberType.PLUS]: true
    })

    // 监听isYearly的变化，任一值发生变化，实现同时开启或同时关闭
    watch(
        () => [isYearly[MemberType.PRO], isYearly[MemberType.PLUS]],
        ([newPro, newPlus], [oldPro, oldPlus]) => {
            if (newPro !== oldPro) {
                isYearly[MemberType.PLUS] = newPro;
            } else if (newPlus !== oldPlus) {
                isYearly[MemberType.PRO] = newPlus;
            }
        },
        { deep: true }
    );


    /** 是否显示禁用升级弹窗 */
    const upgradeDisabledModalOpen = ref(false);
    /** 禁用升级弹窗提示的会员版本类型 */
    const upgradeDisabledModalType = ref<MemberType | null>(null);

    // 获取当前套餐的算力描述
    const getPowerValue = (pkg: Package, returnType: 'object' | 'text' = 'object') => {
        // 根据是否按年购买获取对应的算力信息
        const powerInfo = isYearly[pkg.type as keyof typeof isYearly]
            ? pkg.yearlyPower
            : pkg.monthPower;

        // 如果没有算力信息，返回默认值
        if (!powerInfo) {
            return returnType === 'text' ? '每月0算力' : { amount: 0, unit: '月' };
        }

        const amount = powerInfo.amount;
        const unit = powerInfo.period || '月';

        // 根据返回类型决定返回格式
        if (returnType === 'text') {
            // 如果套餐是免费版本，则返回"xx算力"
            if (pkg.type === MemberType.FREE) {
                return `${amount}算力`;
            }
            return `每${unit}${amount}算力`;
        } else {
            return {
                amount,
                unit
            };
        }
    };

    // 获取当前套餐的算力描述
    const getPowerDescription = (pkg: Package): string => {
        // 根据是否按年购买获取对应的算力信息
        const powerInfo = isYearly[pkg.type as keyof typeof isYearly]
            ? pkg.yearlyPower
            : pkg.monthPower;

        return powerInfo?.description || '';
    };

    const { isActive: isShowActivity } = useActivityCheck();

    /**
     * 是否参与过活动Pro套餐
     */
    const isParticipatedPro = computed(() => {
        return userStore.sources.activityPro;
    });

    /**
     * 是否参与过活动Plus套餐
     */
    const isParticipatedPlus = computed(() => {
        return userStore.sources.activityPlus;
    });

    const getPackageMoneyActivityParams = {
        isShowActivity: isShowActivity.value,
        isParticipatedPro: isParticipatedPro.value,
        isParticipatedPlus: isParticipatedPlus.value
    };
    

    const packages = ref<Package[]>([
        {
            id: 'free',
            type: 'free',
            name: '免费版',
            price: 0,
            period: '月',
            subTitle: '永久',
            monthPower: {
                amount: 30,
                description: '约生成 30 张图片',
                period: '月'
            },
            yearlyPower: {
                amount: 300,
                description: '约生成 300 张图片',
                period: '月'
            },
            features: [
                {
                    title: '算力充值',
                    icon: grayChecked
                },
                // {
                //     title: '原图下载',
                //     description: '有水印',
                //     icon: grayChecked
                // },
                {
                    title: '最多同时生图',
                    description: '2张',
                    icon: grayChecked
                },
                {
                    title: '作品历史记录保存',
                    description: '7天',
                    icon: grayChecked
                },
                {
                    title: '云空间',
                    description: '1G',
                    icon: grayChecked
                }
            ],
            buttonText: '免费使用',
            buttonStatus: 'disabled'
        },
        {
            id: MemberType.PRO,
            type: MemberType.PRO,
            name: '标准版',
            price: getPackageMoney('vip', 'subscribe', {...getPackageMoneyActivityParams}),
            // 月续费价格
            monthlyRenewPrice: getPackageMoney('vip', 'subscribe', { isRenew: true, ...getPackageMoneyActivityParams }),
            // 年续费折后价格
            yearlyPrice: getPackageMoney('vip', 'year', { ...getPackageMoneyActivityParams }),
            // 年续费原价
            yearlyOriginalPrice: getPackageOrigin('vip', 'year'),
            period: '月',
            discount: {
                enabled: true,
                value: 6,
                label: '限时 6 折',
                originalPrice: 1188,
                discountedPrice: 832
            },
            promotion: '按年购买（买 1 年送 2 月）',
            monthPower: {
                amount: 3000,
                description: '约生成 3000 张图片',
                rechargeBonus: 25,
                period: '月'
            },
            yearlyPower: {
                amount: 3000,
                description: '约生成 3000 张图片',
                rechargeBonus: 25,
                period: '月'
            },
            features: [
                {
                    title: '算力充值',
                    description: '额外加赠25%',
                    icon: grayChecked
                },
                {
                    title: '高清、超清下载',
                    description: '',
                    icon: grayChecked
                },
                {
                    title: '最多同时生图',
                    description: '4张',
                    icon: grayChecked
                },
                {
                    title: '并行生图任务',
                    description: '2个',
                    icon: grayChecked
                },
                {
                    title: '专享生图加速通道',
                    icon: grayChecked
                },
                {
                    title: '模型训练',
                    icon: grayChecked
                },
                {
                    title: '作品历史记录保存',
                    description: '90天',
                    icon: grayChecked
                },
                {
                    title: '云空间',
                    description: '10G',
                    icon: grayChecked
                }
            ],
            buttonText: '立即开通',
            buttonStatus: 'primary'
        },
        {
            id: MemberType.PLUS,
            type: MemberType.PLUS,
            name: '专业版',
            price: getPackageMoney('svip', 'halfSubscribe', { ...getPackageMoneyActivityParams }),
            // 月续费价格
            monthlyRenewPrice: getPackageMoney('svip', 'halfSubscribe', { isRenew: true, ...getPackageMoneyActivityParams }),
            // 月费原价
            monthlyOriginalPrice: getPackageOrigin('svip', 'halfSubscribe'),
            yearlyPrice: getPackageMoney('svip', 'year', { ...getPackageMoneyActivityParams }),
            yearlyOriginalPrice: getPackageOrigin('svip', 'year'),
            period: '半月',
            subTitle: '连续包半月',
            discount: {
                enabled: true,
                value: 6,
                label: '限时 6 折',
                originalPrice: 2388,
                discountedPrice: 1672
            },
            promotion: '按年购买（买 1 年送 2 月）',
            monthPower: {
                amount: 4500,
                description: '无限使用基础功能生图',
                rechargeBonus: 100,
                period: '半月'
            },
            yearlyPower: {
                amount: 9000,
                description: '无限使用基础功能生图',
                rechargeBonus: 100,
                period: '月'
            },
            features: [
                {
                    title: '算力充值',
                    description: '额外加赠100%',
                    icon: goldenChecked
                },
                {
                    title: '高清、超清下载',
                    description: '',
                    icon: goldenChecked
                },
                {
                    title: '最多同时生图',
                    description: '4张',
                    icon: goldenChecked
                },
                {
                    title: '并行生图任务',
                    description: '4个',
                    icon: goldenChecked
                },
                {
                    title: '专享生图加速通道',
                    icon: goldenChecked
                },
                {
                    title: '模型训练',
                    description: '算力消耗8折优惠',
                    icon: goldenChecked
                },
                {
                    title: '作品历史记录保存',
                    description: '180天',
                    icon: goldenChecked
                },
                {
                    title: '云空间',
                    description: '50G',
                    icon: goldenChecked
                }
            ],
            buttonText: '立即开通',
            buttonStatus: 'primary'
        },
        {
            id: 'team',
            type: 'team',
            name: '团队版',
            price: null,
            period: '',
            subTitle: '自订',
            discount: {
                enabled: true,
                value: 5,
                label: '最低 5 折',
                originalPrice: 0,  // 团队版价格需要定制
                discountedPrice: 0
            },
            description: '适用于工作室或企业，最高每年可省 50% 费用',
            monthPower: {
                amount: 0,
                description: '算力按团队规模定制'
            },
            yearlyPower: {
                amount: 0,
                description: '算力按团队规模定制'
            },
            features: [
                {
                    title: '享人数阶梯折扣',
                    description: '来购人数越多，折扣越大！最低享5折优惠！',
                    icon: shareDiscountsForMembers
                },
                {
                    title: '会员权益共享',
                    description: '一人支付，全团队共享算力，会员权益！',
                    icon: memberBenefitsSharing
                },
                {
                    title: '灵活席位管理',
                    description: '购买者自动升级为管理员，灵活增减团队成员！',
                    icon: spiritSeatManagement
                },
                {
                    title: '团队空间高效协作',
                    description: '模型、作品一键共享至团队成员，协作无障碍！',
                    icon: teamSpaceEfficiency
                },
                {
                    title: '团队专属VIP客服',
                    description: '加入专属VIP服务群，享受vip个性化咨询服务！',
                    icon: teamExclusiveVIPService
                }
            ],
            buttonText: '立即开通',
            buttonStatus: 'primary'
        }
    ])

    /**
 * 获取是否存在对公支付订单
 */
    const judgeExistCorporateOrder = async () => {
        const res = await getExistCorporateOrder();
        return res.data;
    }

    const handlePackageSelection = async (pkg: Package, btnTxt: ButtonText) => {

        if (pkg.type === 'team') {
            if (teamEditionStore.isTeamVersion) {
                // 团队试用版本，算力用完或者已过期，弹出开通会员弹窗
                const teamTrialStore = useTeamTrialStore();
                const { isTrialExpired, isPowerExhausted } = storeToRefs(teamTrialStore);

                if (isTrialExpired.value || isPowerExhausted.value) {
                    teamEditionStore.handleOpenTeamEdition();
                    return
                }
                // 切换至团队版
                teamEditionStore.switchUserType();
            } else {
                const payStore = usePayStore();

                teamEditionStore.modalType = teamEditionStore.isTeamVersion ? ModalType.ADD_SEAT : ModalType.OPEN_TEAM_EDITION;

                const corporateOrder = await judgeExistCorporateOrder();
                if (corporateOrder?.transactionId) {
                    // corporateOrder.orderStatus == NOTPAY 未支付
                    if (corporateOrder.orderStatus == 'NOTPAY') {
                        teamEditionStore.corporateTransferOrderInfo = { orderId: corporateOrder.transactionId };
                        teamEditionStore.handleOpenUploadReceipt();
                        return false
                    }

                    // corporateOrder.orderStatus == WAIT_CORPORATE_CONFIRM 待确认
                    if (corporateOrder?.orderStatus == 'WAIT_CORPORATE_CONFIRM') {
                        teamEditionStore.handleOpenPendingReceipt(corporateOrder);
                        return false;
                    }
                }

                useEventTracking({
                    event: 'click',
                    eventValue: {
                        view: 'view_power',
                        button: 'member_team_open',
                    }
                });

                // 打开开通团队版弹窗
                payStore.useProductModal('groupMember');
            }
            return
        }

        // 按钮是否置灰
        if (pkg.buttonStatus === 'disabled') {
            console.log('按钮置灰')
            return
        }

        /**
         * 连续包月、包半月会员，点击「续费会员」、「升级会员」，均弹出如图所示提示弹窗（全量调整）
         */
        // // 已开通专业版半月会员,且非年费,点击开通时
        const isOnlyHalfSubscribePlus = userStore.isHalfSubscribePlus
        // // 已开通标准版连续包月会员,且非年费,点击开通时
        const isOnlyMonthPro = userStore.isMonthPro
        if ((isOnlyHalfSubscribePlus || isOnlyMonthPro) && (pkg.buttonStatus === 'renew' || pkg.buttonStatus === 'upgrade')) {
            const currentType = userStore.currentMemberInfo.type
            upgradeDisabledModalType.value = currentType
            upgradeDisabledModalOpen.value = true
            return
        }


        const payStore = usePayStore();
        // 处理套餐选择逻辑
        let timeMode;
        if (isYearly[pkg.type as keyof typeof isYearly]) {
            timeMode = 'year_v1';
        } else {
            timeMode = pkg.type === MemberType.PLUS ? 'halfSubscribe' : 'subscribe'
        }
        const key = pkg.type === MemberType.PLUS ? 'svip' : 'vip';


        const buttonStatus = getButtonStatus(btnTxt)
        useEventTracking({
            event: 'click',
            eventValue: {
                view: 'view_power',
                button: buttonStatus || '',
            }
        });

        payStore.usePayMemberModal({ key, timeMode, source: 'powerPage' });
    }

    const getButtonInfo = (pkg: Package) => {
        // 团队版处理逻辑保持不变
        if (pkg.type === 'team') {

            // 团队试用版本，算力用完或者已过期，弹出开通会员弹窗
            const teamTrialStore = useTeamTrialStore();
            const { isTrialVersion, isTrialExpired, isPowerExhausted } = storeToRefs(teamTrialStore);
            if (isTrialVersion.value) {
                if (isTrialExpired.value) {
                    return TRIAL_EXPIRED_BUTTON_TEXT
                } else if (isPowerExhausted.value) {
                    return POWER_EXHAUSTED_BUTTON_TEXT
                } else {
                    return '已开通试用，直接进入团队版'
                }
            }
            return teamEditionStore.isTeamVersion ? '已开通，直接进入团队版' : '立即开通'
        }

        // 获取当前用户会员信息
        const { type: currentType, cycle: currentCycle } = userStore.currentMemberInfo;

        // 如果是免费用户，显示"免费使用"
        if (currentType === MemberType.FREE) {
            if (pkg.type === MemberType.FREE) {
                return '免费使用';
            } else {
                return '立即开通';
            }
        }

        // 构建目标会员信息
        const targetType = pkg.type as MemberType; // 转换为MemberType枚举
        // 根据是否按年购买确定目标周期
        const targetCycle = isYearly[targetType as keyof typeof isYearly] ?
            MemberCycle.YEAR :
            (targetType === MemberType.PLUS ? MemberCycle.HALF_SUBSCRIBE : MemberCycle.SUBSCRIBE);

        // 判断用户是A组还是B组
        const isAUser = !isPlanB.value;

        // 使用judgeMemberButtonState获取按钮状态
        const buttonState = judgeMemberButtonState({
            currentType,
            currentCycle,
            targetType,
            targetCycle,
            isAUser
        });

        // 根据按钮状态更新包信息
        const packageToUpdate = packages.value.find(item => item.id === pkg.type);
        if (packageToUpdate) {
            // packageToUpdate.buttonStatus = buttonState.status === ButtonStatus.NORMAL ? 'primary' : 'disabled';
            packageToUpdate.buttonStatus = buttonState.status;
        }

        // 返回按钮文本
        return buttonState.text;
    }

    // 新增更新价格方法
    const updatePackagePrices = () => {
        packages.value = packages.value.map(pkg => {
            if (pkg.type === MemberType.PRO) {
                return {
                    ...pkg,
                    price: getPackageMoney('vip', 'subscribe', { ...getPackageMoneyActivityParams }),
                    monthlyRenewPrice: getPackageMoney('vip', 'subscribe', { isRenew: true, ...getPackageMoneyActivityParams }),
                    yearlyPrice: getPackageMoney('vip', 'year', { ...getPackageMoneyActivityParams }),
                    yearlyOriginalPrice: getPackageOrigin('vip', 'year'),
                    buttonText: getButtonInfo(pkg)
                }
            } else if (pkg.type === MemberType.PLUS) {
                return {
                    ...pkg,
                    price: getPackageMoney('svip', 'halfSubscribe', { ...getPackageMoneyActivityParams }),
                    monthlyRenewPrice: getPackageMoney('svip', 'halfSubscribe', { isRenew: true, ...getPackageMoneyActivityParams }),
                    monthlyOriginalPrice: getPackageOrigin('svip', 'halfSubscribe'),
                    yearlyPrice: getPackageMoney('svip', 'year', { ...getPackageMoneyActivityParams }),
                    yearlyOriginalPrice: getPackageOrigin('svip', 'year'),
                    buttonText: getButtonInfo(pkg)
                }
            }
            return pkg;
        });
    }

    return {
        packages,
        handlePackageSelection,
        isYearly,
        isPlanB,
        getButtonInfo,
        upgradeDisabledModalOpen,
        upgradeDisabledModalType,
        updatePackagePrices,
        getPowerValue,
        getPowerDescription
    }
})